import { useState, useEffect, useRef, useMemo } from 'react';

interface BondItem {
  team: string;
  text: string;
}

interface BondAutoAssignDisplayProps {
  allBonds: BondItem[];
  winners: BondItem[];
}

const TEAM_TEXT_COLORS: Record<string, string> = {
  BLUE: '#60a5fa',    // blue-400
  RED: '#f87171',     // red-400
  GREEN: '#4ade80',   // green-400
  YELLOW: '#facc15',  // yellow-400
};

const TEAM_GLOW_COLORS: Record<string, string> = {
  BLUE: 'rgba(96,165,250,0.4)',
  RED: 'rgba(248,113,113,0.4)',
  GREEN: 'rgba(74,222,128,0.4)',
  YELLOW: 'rgba(250,204,21,0.4)',
};

interface PositionedBond extends BondItem {
  x: number;
  y: number;
  orbitRadius: number;
  orbitSpeed: number;
  orbitOffset: number;
  isWinner: boolean;
}

function BondAutoAssignDisplay({ allBonds, winners }: BondAutoAssignDisplayProps) {
  const [phase, setPhase] = useState<'scatter' | 'select'>('scatter');
  const containerRef = useRef<HTMLDivElement>(null);
  const animFrameRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const elementsRef = useRef<Map<number, HTMLDivElement>>(new Map());

  // Generate random positions for all bonds, avoiding overlaps
  const positionedBonds = useMemo(() => {
    const margin = 120;
    const maxWidth = 1920 - margin * 2;
    const maxHeight = 1080 - margin * 2;
    const minDistance = 130;
    const positions: { x: number; y: number }[] = [];

    return allBonds.map((bond): PositionedBond => {
      let x: number, y: number;
      let attempts = 0;

      do {
        x = margin + Math.random() * maxWidth;
        y = margin + Math.random() * maxHeight;
        attempts++;

        const tooClose = positions.some(pos => {
          const dx = pos.x - x;
          const dy = pos.y - y;
          return Math.sqrt(dx * dx + dy * dy) < minDistance;
        });

        if (!tooClose || attempts > 60) break;
      } while (true);

      positions.push({ x, y });

      const isWinner = winners.some(w => w.team === bond.team && w.text === bond.text);

      return {
        ...bond,
        x,
        y,
        orbitRadius: 8 + Math.random() * 12,
        orbitSpeed: 0.8 + Math.random() * 1.2,
        orbitOffset: Math.random() * Math.PI * 2,
        isWinner,
      };
    });
  }, [allBonds, winners]);

  // Orbit animation loop (phase 1 only)
  useEffect(() => {
    startTimeRef.current = performance.now();

    const animate = (now: number) => {
      const elapsed = (now - startTimeRef.current) / 1000;

      positionedBonds.forEach((bond, idx) => {
        const el = elementsRef.current.get(idx);
        if (!el) return;

        const angle = elapsed * bond.orbitSpeed + bond.orbitOffset;
        const dx = Math.cos(angle) * bond.orbitRadius;
        const dy = Math.sin(angle) * bond.orbitRadius;

        el.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
      });

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [positionedBonds]);

  // Phase transition: scatter -> select after 3.5s
  useEffect(() => {
    const timer = setTimeout(() => {
      // Stop orbit animation
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);

      // Reset transforms before transition
      positionedBonds.forEach((_, idx) => {
        const el = elementsRef.current.get(idx);
        if (el) {
          el.style.transform = 'translate(-50%, -50%)';
        }
      });

      setPhase('select');
    }, 3500);

    return () => clearTimeout(timer);
  }, [positionedBonds]);

  // Calculate centered positions for winners (percentage-based)
  const winnerPositions = useMemo(() => {
    const totalWinners = winners.length;
    const spacingPercent = 8; // % of viewport height
    const startY = 50 - ((totalWinners - 1) * spacingPercent) / 2;

    return winners.map((_, idx) => ({
      x: 50, // 50% = center
      y: startY + idx * spacingPercent,
    }));
  }, [winners]);

  const setRef = (idx: number) => (el: HTMLDivElement | null) => {
    if (el) {
      elementsRef.current.set(idx, el);
    } else {
      elementsRef.current.delete(idx);
    }
  };

  return (
    <div
      ref={containerRef}
      className="h-screen w-screen display-dark-bg relative overflow-hidden"
    >
      {positionedBonds.map((bond, idx) => {
        const isWinner = bond.isWinner;
        const shouldFade = phase === 'select' && !isWinner;

        // In select phase, winners move to center
        let style: React.CSSProperties;
        if (phase === 'select' && isWinner) {
          const winnerIdx = winners.findIndex(w => w.team === bond.team && w.text === bond.text);
          const pos = winnerPositions[winnerIdx] || { x: 50, y: 50 };
          style = {
            position: 'absolute',
            left: `${pos.x}%`,
            top: `${pos.y}%`,
            color: TEAM_TEXT_COLORS[bond.team] || '#e5e5e5',
            transition: 'all 1.5s ease-in-out',
            opacity: 1,
            transform: 'translate(-50%, -50%) scale(1.4)',
            zIndex: 50,
            textShadow: `0 0 20px ${TEAM_GLOW_COLORS[bond.team] || 'rgba(255,255,255,0.3)'}`,
          };
        } else {
          style = {
            position: 'absolute',
            left: bond.x,
            top: bond.y,
            color: TEAM_TEXT_COLORS[bond.team] || '#e5e5e5',
            transition: phase === 'select' ? 'opacity 1s ease-out' : 'none',
            opacity: shouldFade ? 0 : 0.85,
          };
        }

        return (
          <div
            key={`${bond.team}-${idx}`}
            ref={setRef(idx)}
            className={`font-heading whitespace-nowrap ${
              phase === 'select' && isWinner
                ? 'text-2xl font-bold'
                : 'text-lg font-semibold'
            }`}
            style={style}
          >
            {bond.text}
          </div>
        );
      })}

      {/* Spotlight effect on winners during select phase */}
      {phase === 'select' && (
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[500px] rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(139,105,20,0.25) 0%, rgba(0,0,0,0) 70%)',
              animation: 'pulse 2s infinite',
              transition: 'opacity 1s ease-in',
            }}
          />
        </div>
      )}
    </div>
  );
}

export default BondAutoAssignDisplay;
